// Printable salary slip and salary certificate. These replace the server-side
// PDF generator: the document opens in a new tab, and "Save as PDF" in the
// print dialog produces the file.
import { aed, dateLabel, monthLabel } from "@/lib/format";

const COMPANY = {
  name: "MediLearn Tech Skills Training",
  address: "Business Bay, Dubai, United Arab Emirates",
  email: "hr@medilearn.ae",
};

const esc = (s) => String(s ?? "—").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const shell = (title, body) => `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  *{box-sizing:border-box} body{margin:0;background:#EEF2F8;font:14px/1.5 Inter,system-ui,sans-serif;color:#1e293b}
  .page{max-width:800px;margin:32px auto;background:#fff;padding:48px;border-radius:16px;box-shadow:0 20px 50px -20px rgba(15,23,42,.25)}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #225881;padding-bottom:18px;margin-bottom:28px}
  .logo{font:800 26px system-ui;letter-spacing:-.02em} .logo b{color:#225881} .logo span{color:#F5A623}
  .muted{color:#64748b;font-size:12px} h1{font-size:20px;margin:0 0 4px;color:#225881}
  table{width:100%;border-collapse:collapse;margin:16px 0} th,td{padding:9px 12px;text-align:left;border-bottom:1px solid #e2e8f0}
  th{background:#f1f5f9;font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#475569}
  td.n{text-align:right;font-variant-numeric:tabular-nums} .grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 32px;margin-bottom:8px}
  .grid div{display:flex;justify-content:space-between;border-bottom:1px dashed #e2e8f0;padding:4px 0} .grid div span:first-child{color:#64748b}
  .net{margin-top:20px;background:#225881;color:#fff;border-radius:12px;padding:18px 22px;display:flex;justify-content:space-between;align-items:center}
  .net strong{font-size:24px;color:#F5A623} .foot{margin-top:40px;font-size:11px;color:#94a3b8;text-align:center}
  .toolbar{max-width:800px;margin:24px auto 0;display:flex;justify-content:flex-end;gap:8px}
  .toolbar button{border:0;border-radius:10px;padding:10px 18px;font-weight:600;cursor:pointer;background:#225881;color:#fff}
  p.letter{margin:0 0 14px} .sign{margin-top:48px}
  @media print{body{background:#fff}.page{margin:0;box-shadow:none;border-radius:0;padding:24px}.toolbar{display:none}}
</style></head>
<body><div class="toolbar"><button onclick="window.print()">Print / Save as PDF</button></div>
<div class="page">${body}</div></body></html>`;

const header = (title, sub) => `
  <div class="head">
    <div><div class="logo"><b>Medi</b><span>Learn</span></div><div class="muted">${esc(COMPANY.address)}<br>${esc(COMPANY.email)}</div></div>
    <div style="text-align:right"><h1>${esc(title)}</h1><div class="muted">${esc(sub)}</div></div>
  </div>`;

function slipHtml(s) {
  const earnings = [
    ["Basic Salary", s.basic], ["Housing Allowance", s.housing], ["Transport Allowance", s.transport],
    ["Communication Allowance", s.communication], ["Other Allowance", s.other_allowance], ["Bonus", s.bonus],
  ];
  const deductions = [
    [`Loss of Pay (${s.unpaid_leave_days} day${s.unpaid_leave_days === 1 ? "" : "s"})`, s.lop_deduction],
    ["Loan / Advance", s.loan], ["Other Deductions", s.other_deduction],
  ];
  const rows = (list) => list.map(([k, v]) => `<tr><td>${esc(k)}</td><td class="n">${aed(v)}</td></tr>`).join("");
  return shell(`Salary Slip — ${s.employee_name} — ${s.month}`, `
    ${header("Salary Slip", monthLabel(s.month))}
    <div class="grid">
      <div><span>Employee</span><b>${esc(s.employee_name)}</b></div><div><span>Employee ID</span><b>${esc(s.employee_id)}</b></div>
      <div><span>Designation</span><b>${esc(s.designation)}</b></div><div><span>Department</span><b>${esc(s.department)}</b></div>
      <div><span>Emirates ID</span><b>${esc(s.emirates_id)}</b></div><div><span>Labour Card</span><b>${esc(s.labour_card)}</b></div>
      <div><span>Bank</span><b>${esc(s.bank_name)}</b></div><div><span>IBAN</span><b>${esc(s.iban)}</b></div>
      <div><span>Working Days</span><b>${esc(s.working_days)}</b></div><div><span>WPS Status</span><b>${esc(s.wps_status)}</b></div>
    </div>
    <table><thead><tr><th>Earnings</th><th class="n">AED</th></tr></thead><tbody>${rows(earnings)}
      <tr><th>Gross Salary</th><th class="n">${aed(s.gross)}</th></tr></tbody></table>
    <table><thead><tr><th>Deductions</th><th class="n">AED</th></tr></thead><tbody>${rows(deductions)}
      <tr><th>Total Deductions</th><th class="n">${aed(s.total_deductions)}</th></tr></tbody></table>
    <div class="net"><span>Net Pay</span><strong>AED ${aed(s.net_pay)}</strong></div>
    <div class="foot">This is a system-generated document and does not require a signature.</div>`);
}

function certificateHtml(c) {
  return shell(`Salary Certificate — ${c.employee_name}`, `
    ${header("Salary Certificate", `Issued ${dateLabel(c.created_at)}`)}
    <p class="letter"><b>To:</b> ${esc(c.addressed_to)}</p>
    <p class="letter"><b>Subject:</b> Salary Certificate — ${esc(c.purpose)}</p>
    <p class="letter">To whom it may concern,</p>
    <p class="letter">This is to certify that <b>${esc(c.employee_name)}</b> (Emirates ID ${esc(c.emirates_id)}) has been employed with
      ${esc(COMPANY.name)} as <b>${esc(c.designation)}</b> in the ${esc(c.department)} department since ${dateLabel(c.date_of_joining)}.</p>
    <table><tbody>
      <tr><td>Monthly gross salary</td><td class="n"><b>AED ${aed(c.monthly_salary)}</b></td></tr>
      <tr><td>Annual gross salary</td><td class="n"><b>AED ${aed(c.annual_salary)}</b></td></tr>
    </tbody></table>
    <p class="letter">This certificate is issued at the employee's request for ${esc(c.purpose.toLowerCase())} purposes and carries no
      financial liability on the part of the company.</p>
    <div class="sign"><b>HR Department</b><div class="muted">${esc(COMPANY.name)}</div></div>
    <div class="foot">Reference ${esc(c.id)}</div>`);
}

function openDocument(html, autoPrint) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  if (autoPrint) w.addEventListener("load", () => w.print(), { once: true });
  return true;
}

export const openSalarySlip = (slip, autoPrint = false) => openDocument(slipHtml(slip), autoPrint);
export const openCertificate = (cert, autoPrint = false) => openDocument(certificateHtml(cert), autoPrint);
